/**
 * @fileoverview Reads the remembered-device key Amplify stored after
 * the last successful sign-in.
 *
 * @module auth/rememberedDevice
 *
 * Amplify v6 confirms the user's device with Cognito after a
 * successful CUSTOM_AUTH sign-in, then stashes the device key in
 * localStorage under
 * `CognitoIdentityServiceProvider.{clientId}.{cognitoUsername}.deviceKey`,
 * with the `LastAuthUser` pointer in
 * `CognitoIdentityServiceProvider.{clientId}.LastAuthUser`.
 *
 * On the NEXT sign-in we pass that key through `clientMetadata` so the
 * `DefineAuthChallenge` Lambda can call `AdminListDevices` and decide
 * whether to skip the magic-link MFA. Cognito does NOT inject a
 * DEVICE_PASSWORD_VERIFIER step into CUSTOM_AUTH flows, so we do this
 * skip explicitly via clientMetadata + admin lookup.
 *
 * If the user signs into the same browser as a DIFFERENT account, the
 * stored key won't match that user's device list and the Lambda falls
 * through to the magic-link challenge — correct behavior.
 */

import { authConfig } from '../config/auth-config.ts';

export function readStoredDeviceKey(): string | undefined {
  try {
    if (typeof localStorage === 'undefined') {
      return undefined;
    }
    const clientId = authConfig.userPoolClientId;
    const prefix = `CognitoIdentityServiceProvider.${clientId}.`;
    const suffix = '.deviceKey';
    // Scan for any device-key entry under our client. We don't rely on
    // `LastAuthUser` because Amplify's `signOut()` clears that pointer
    // but leaves the deviceKey/deviceGroupKey/randomPasswordKey trio
    // intact — so after an explicit sign-out, LastAuthUser is gone but
    // the device record we want to surface is still there.
    //
    // If multiple users have signed in on this browser, multiple
    // device-key entries can coexist (one per Cognito Username). We
    // return the first one we find; DefineAuthChallenge then validates
    // it against AdminListDevices for whichever user is currently
    // authenticating, so cross-user mismatches fall through to the
    // magic-link flow naturally.
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (
        key !== null &&
        key.startsWith(prefix) &&
        key.endsWith(suffix) &&
        // Guard against false matches on a user named literally "."
        // or substrings that contain ".deviceKey" in the middle.
        key.length > prefix.length + suffix.length
      ) {
        const value = localStorage.getItem(key);
        if (value !== null && value.length > 0) {
          return value;
        }
      }
    }
    return undefined;
  } catch {
    // localStorage can throw in restricted contexts (e.g., private
    // mode iOS Safari with full quota). Treat as "no remembered
    // device" — worst case the user gets the magic-link prompt once.
    return undefined;
  }
}
